/**
 * What editing a Stat Dictionary entry means, kept out of the component.
 *
 * The Stat Dictionary screen could already change two things in place: a
 * Direction from a select, and a Weight from a text cell. Everything else an
 * entry declares — its display name, its **key**, its aliases,
 * its description — was read-only, and the client asked for all of it.
 *
 * A form over ten fields is not nine more cells, and two things change when it
 * becomes one:
 *
 * **It saves in one request, so the body has to carry only what moved.** The
 * naive body is every field every time, and that is wrong in a way no
 * screenshot shows. `key` restated unchanged reaches the server's rename
 * cascade on every save of every field; `aliases` resent unchanged is a write
 * to the column discovery auto-binds from. `changesFor` is the diff, and a
 * draft nobody edited produces `{}`.
 *
 * **A key is not a label, so the screen has to say what moving it costs.**
 * `Binding.stat_key` names an entry by value with no foreign key — it cannot
 * have one, since an entry is identified by sport *and* key (ADR-0017) — so the
 * server carries every reference across in one transaction. That makes a rename
 * work. It does not make it free: `app/domain/ranking.py` sorts its published
 * column list on the stat key, so a fully cascaded rename publishes the same
 * headers and the same values **in a different order**, on every source bound
 * to the statistic, at its next firing. `renameSummary` is what the operator
 * reads before pressing the button rather than after.
 *
 * No imports, so `npm test` can execute it directly: the frontend ships no test
 * runner and bare `node --experimental-strip-types` cannot load React or Vite.
 */

/** Mirrors `api.ts`'s `Direction`, restated so this module imports nothing. */
type Direction = "higher_is_better" | "lower_is_better";

/**
 * The entry as the editor needs to read it.
 *
 * Structural rather than `StatEntry`, so this module neither imports `api.ts`
 * nor has to be widened every time a column the editor does not touch is added
 * to the dictionary.
 */
export interface StatEntryLike {
  key: string;
  display_name: string;
  direction: Direction;
  default_weight: number | null;
  description: string | null;
  aliases: string[];
  excluded_position_groups: string[];
}

/**
 * One row's editor, as it is typed.
 *
 * Weight and aliases are strings because they are *boxes*: parsing each
 * keystroke into a number fights a half-typed "0." and parsing each keystroke
 * into a list fights a half-typed comma. The same reason the existing weight
 * cell holds text, and the same reason the add form's aliases box does.
 */
export interface StatDraft {
  key: string;
  display_name: string;
  direction: Direction;
  weight: string;
  description: string;
  aliases: string;
  /**
   * Which Canonical Position Groups the statistic does not rank.
   *
   * A list rather than text, unlike the two boxes above it. Those are strings
   * because they are typed and a half-finished "0." or trailing comma has to
   * survive the keystroke that made it; this is a set of checkboxes, where
   * every intermediate state is already a valid value.
   */
  excluded: string[];
}

/** The patch body, which is `StatEntryPatch` on the server minus `sport`. */
export interface StatPatch {
  key?: string;
  display_name?: string;
  direction?: Direction;
  default_weight?: number | null;
  description?: string | null;
  aliases?: string[];
  excluded_position_groups?: string[];
}

export type StatChanges =
  | { ok: true; patch: StatPatch; renamed: boolean }
  | { ok: false; field: keyof StatPatch; message: string };

/** Open the editor on what the entry holds. */
export function draftFor(entry: StatEntryLike): StatDraft {
  return {
    key: entry.key,
    display_name: entry.display_name,
    direction: entry.direction,
    // Blank, not "1". Null is a review state ADR-0040 keeps distinct from 1.0
    // written out, and an editor that filled it in would turn every entry
    // somebody merely opened into one that declares a weight.
    weight: entry.default_weight == null ? "" : String(entry.default_weight),
    description: entry.description ?? "",
    aliases: entry.aliases.join(", "),
    excluded: [...entry.excluded_position_groups],
  };
}

/**
 * Whether two selections of position groups say the same thing.
 *
 * Order-insensitive, and that is the point rather than a convenience: the server
 * stores them in the sport's own order and a draft holds them in whatever order
 * the boxes were ticked, so a literal comparison would call every re-ticked row
 * edited and send a patch that writes back what is already there.
 *
 * `sameGroups` in `position-exclusions.ts` is the same three lines, and they are
 * restated here for the reason `parseWeight` above restates
 * `stat-weight.ts::parseWeightPercent`: this module imports nothing, so bare
 * `node --experimental-strip-types` can execute it, and a value import between
 * `src/` modules does not resolve without a file extension that
 * `tsconfig.json` forbids writing.
 */
function sameGroups(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const held = new Set(a);
  return b.every((group) => held.has(group));
}

/** Aliases as the server stores them: trimmed, lowercased, blanks dropped. */
export function parseAliases(text: string): string[] {
  return text
    .split(",")
    .map((alias) => alias.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * A weight box as a number, or why it cannot be read as one.
 *
 * The same rules `stat-weight.ts::parseWeightPercent` applies, restated rather
 * than imported only because that module is reached from the Fields screens and
 * this one has no business widening its surface. Blank is `null` — nothing
 * declared — and not nought, which ADR-0040 refuses outright.
 */
function parseWeight(text: string): { ok: true; weight: number | null } | { ok: false; message: string } {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, weight: null };

  // `Number` rather than `parseFloat`, which reads "1.2.3" as 1.2 and "20%" as
  // 20 — taking the leading digits of something nobody meant as a number.
  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return { ok: false, message: "That is not a number. A weight is a percentage, like 20 or 7.5." };
  }
  if (value <= 0) {
    // A statistic that should not be scored is `carried` or `ignored`, which the
    // Field Role already says. Nought here divides by zero in the ranker.
    return { ok: false, message: "A weight has to be above nought. Leave it blank to declare none." };
  }
  if (value > 100) {
    return { ok: false, message: "A weight is a percentage, so it cannot be above 100." };
  }
  return { ok: true, weight: value };
}

/**
 * What a save should send, or the first box that is wrong.
 *
 * Only the differences, and `renamed` beside them because whether the key moved
 * decides whether the screen asks a second time — a comparison the caller
 * should not be re-deriving.
 */
export function changesFor(entry: StatEntryLike, draft: StatDraft): StatChanges {
  const key = draft.key.trim();
  if (!key) {
    return { ok: false, field: "key", message: "A statistic needs a key. It is what every source refers to it by." };
  }

  const displayName = draft.display_name.trim();
  if (!displayName) {
    return {
      ok: false,
      field: "display_name",
      message: "A statistic needs a display name. It is the column header in every published file.",
    };
  }

  // **Refused only where the box was actually edited**, which the other fields
  // get for free by being diffed and this one does not, because it is parsed
  // before it can be compared.
  //
  // The screen's ceiling is 100 and the API's is 1000 — deliberately, and
  // argued at `BindingIn.weight`: the form is the tighter gate because a
  // percentage is the scale a person can hold in their head, while the column
  // has to stay able to hold what a Vocabulary or a Source Template already
  // wrote. Refusing an untouched value would make a weight of 150 unsaveable
  // *and unfixable*: the operator who opened the row to correct its key would
  // be blocked by a number they never typed and cannot reach from here.
  const weight = parseWeight(draft.weight);
  const weightMoved = draft.weight.trim() !== draftFor(entry).weight;
  if (!weight.ok && weightMoved) {
    return { ok: false, field: "default_weight", message: weight.message };
  }

  const description = draft.description.trim();
  const aliases = parseAliases(draft.aliases);

  const patch: StatPatch = {};
  if (key !== entry.key) patch.key = key;
  if (displayName !== entry.display_name) patch.display_name = displayName;
  if (draft.direction !== entry.direction) patch.direction = draft.direction;
  // Sent only when the box moved *and* parsed. An untouched out-of-range value
  // is carried through this form untouched rather than refused or rewritten.
  if (weight.ok && weight.weight !== entry.default_weight) {
    patch.default_weight = weight.weight;
  }
  // Emptied means withdrawn, which is `null` and not `""` — the column is
  // nullable and an empty string would be a description nobody wrote.
  if ((description || null) !== (entry.description || null)) {
    patch.description = description || null;
  }
  // **Both sides normalised, because only one of them has been.** The server
  // lowercases what it is sent, so anything leaving this box is stored
  // lowercase — but `vocabulary.install` writes its tuples verbatim, and the
  // shipped vocabularies hold aliases like `Asst Sack` and `Rush TD`. Comparing
  // a normalised draft against a raw stored value makes every such entry read
  // as edited the moment it is opened, so opening one and pressing Save would
  // rewrite the column discovery auto-binds from.
  //
  // Case is not a difference that matters: discovery normalises a name before
  // matching it (`_normalise` in `app/api/discovery.py`), so `Asst Sack` and
  // `asst sack` bind identically. Order is — discovery walks the list and the
  // first match wins — so the comparison keeps it.
  //
  // Joined on a newline rather than a space, because an alias may *contain* a
  // space: `["asst sack"]` and `["asst", "sack"]` are different lists that a
  // space-joined comparison would call equal.
  if (aliases.join("\n") !== parseAliases(entry.aliases.join(",")).join("\n")) {
    patch.aliases = aliases;
  }
  // Sent whole rather than as a difference: the column is the list, so
  // withdrawing the last group is `[]` and not an omission. Compared by
  // membership for the reason `sameGroups` gives.
  if (!sameGroups(draft.excluded, entry.excluded_position_groups)) {
    patch.excluded_position_groups = [...draft.excluded];
  }

  return { ok: true, patch, renamed: patch.key !== undefined };
}

/** One profile as the usage endpoint reports it, structurally. */
export interface StatUsageProfileLike {
  name: string;
  league: string;
  schedule_enabled: boolean;
}

/** What the usage endpoint answers, structurally. */
export interface StatUsageLike {
  binding_count: number;
  eligibility_count: number;
  scheduled_count: number;
  derived_keys: string[];
  profiles: StatUsageProfileLike[];
}

/**
 * What a rename will move, as a sentence — or null where it moves nothing.
 *
 * Null rather than "0 sources" for a statistic nothing uses, because a warning
 * shown over an empty consequence is what teaches an operator to dismiss
 * warnings. The counted figures are deliberately not collapsed into one total:
 * "four sources" reads as editing to redo, and "four sources, two of them on a
 * schedule" reads as two files republishing with nobody watching, which is the
 * decision the operator is actually making.
 */
export function renameSummary(usage: StatUsageLike): string | null {
  const parts: string[] = [];
  const sources = usage.profiles.length;

  if (sources > 0) {
    const plural = sources === 1 ? "source" : "sources";
    let clause = `${sources} ${plural}`;
    if (usage.scheduled_count > 0) {
      clause += `, ${usage.scheduled_count} of them on an enabled schedule,`;
    }
    parts.push(`${clause} will be carried across to the new key.`);
  }

  if (usage.eligibility_count > 0) {
    const plural = usage.eligibility_count === 1 ? "source uses" : "sources use";
    parts.push(
      `${usage.eligibility_count} ${plural} it as their eligibility statistic, which decides who is ranked at all.`,
    );
  }

  if (usage.derived_keys.length > 0) {
    parts.push(`${usage.derived_keys.join(", ")} derive from it per ninety minutes.`);
  }

  if (parts.length === 0) return null;

  // **Said every time, because it survives a correct cascade.** The published
  // columns are sorted on the stat key, not on the display name, so the files
  // keep every header and every value and change their order. A consumer
  // reading by position sees different data in the same column, and nothing in
  // the portal reports it as a change.
  parts.push(
    "Published columns are ordered by key, so these files will keep every header and value and publish them in a different order.",
  );
  return parts.join(" ");
}
